import React from 'react';
import { Grid } from '@material-ui/core'
import { GET_ONE, NumberInput, RecordContextProvider, SimpleForm, useQuery, useRecordContext } from 'react-admin';
import { useFormState } from 'react-final-form';
import { useSelector } from 'react-redux';

import { FF_GRADES, FF_OFFICIALS, FF_SCOREKEEPERS, FF_TIMEKEEPERS } from '@hisports/common/featureFlags';

import Toolbar from '../../common/ra/Toolbar';
import { AssignmentStatusEnumInput } from '../../common/inputs/EnumInputs';
import { FieldDependency } from '../../common/FieldDependency';
import { useRichTranslate } from '../../common/useRichTranslate';
import { SquareAlert } from '../../common/SquareAlert';
import { useFlag } from '../../http';

import { OfficeInput } from '../offices/OfficeInput';
import { AssignSystemInput } from '../assignSystems';
import { AssignFeesInput } from '../assignFees';
import { useAssignmentsContext } from '../games/cards/OfficialsCard/AssignmentsContext';
import { AssignOfficeField, AssignRuleField } from '../assignRules/AssignRuleGrid';

const validate = values => {
  const errors = {};

  if (!values.officeId) errors.officeId = 'ra.validation.required';
  if (!values.systemId) errors.systemId = 'ra.validation.required';
  if (!values.status) errors.status = 'ra.validation.required';

  if (values.minReferee != null) {
    if (values.minReferee < 0) {
      errors.minReferee = 'resources.assignSettings.validations.min_setting_range';
    } else if (values.minReferee % 1 > 0) {
      errors.minReferee = 'resources.assignSettings.validations.invalid_level';
    }
  }

  if (values.minLinesperson != null) {
    if (values.minLinesperson < 0) {
      errors.minLinesperson = 'resources.assignSettings.validations.min_setting_range';
    } else if (values.minLinesperson % 1 > 0) {
      errors.minLinesperson = 'resources.assignSettings.validations.invalid_level';
    }
  }

  if (values.minOfficial != null) {
    if (values.minOfficial < 0) {
      errors.minOfficial = 'resources.assignSettings.validations.min_setting_range';
    } else if (values.minOfficial % 1 > 0) {
      errors.minOfficial = 'resources.assignSettings.validations.invalid_level';
    }
  }

  if (values.minScorekeeper != null) {
    if (values.minScorekeeper < 0) {
      errors.minScorekeeper = 'resources.assignSettings.validations.min_setting_range';
    } else if (values.minScorekeeper % 1 > 0) {
      errors.minScorekeeper = 'resources.assignSettings.validations.invalid_level';
    }
  }

  if (values.minTimekeeper != null) {
    if (values.minTimekeeper < 0) {
      errors.minTimekeeper = 'resources.assignSettings.validations.min_setting_range';
    } else if (values.minTimekeeper % 1 > 0) {
      errors.minTimekeeper = 'resources.assignSettings.validations.invalid_level';
    }
  }

  if (values.minRefereeGrade != null && (values.minRefereeGrade % 1 > 0 || values.minRefereeGrade < 0)) {
    errors.minRefereeGrade = 'resources.assignSettings.validations.invalid_grade';
  }
  if (values.minLinespersonGrade != null && (values.minLinespersonGrade % 1 > 0 || values.minLinespersonGrade < 0)) {
    errors.minLinespersonGrade = 'resources.assignSettings.validations.invalid_grade';
  }

  if (values.minAge != null && (values.minAge % 1 > 0 || values.minAge < 1)) {
    errors.minAge = 'resources.assignSettings.validations.invalid_age';
  }

  return errors;
}

const inputProps = {
  resource: 'gameAssignSettings',
  basePath: '/gameAssignSettings',
  variant: 'outlined',
  margin: 'none',
  fullWidth: true,
}

const SystemInput = props => {
  let helperText;
  const { values } = useFormState();
  const system = useSelector(state => state.admin.resources.assignSystems.data[values?.systemId])
  const { assignments } = useAssignmentsContext();

  if (assignments?.length) {
    const referees = assignments.filter(assignment => assignment.position === 'Referee').length
    const linespersons = assignments.filter(assignment => assignment.position === 'Linesperson').length
    const linespersonsTwo = assignments.filter(assignment => assignment.position === 'LinespersonTwo').length
    const linespersonsThree = assignments.filter(assignment => assignment.position === 'LinespersonThree').length
    const officials = assignments.filter(assignment => assignment.position === 'Official').length
    const scorekeepers = assignments.filter(assignment => assignment.position === 'Scorekeeper').length
    const timekeepers = assignments.filter(assignment => assignment.position === 'Timekeeper').length
    const supervisors = assignments.filter(assignment => assignment.position === 'Supervisor').length

    const mismatch = system && (
      referees > system.referees
      || linespersons > system.linespersons
      || linespersonsTwo > system.linespersonsTwo
      || linespersonsThree > system.linespersonsThree
      || officials > system.officials
      || scorekeepers > system.scorekeepers
      || timekeepers > system.timekeepers
      || supervisors > system.supervisors
    )
    if (mismatch) {
      helperText = 'resources.assignSettings.helpers.systemId'
    }
  }

  return <AssignSystemInput helperText={helperText} {...props} />
}

const FeesInput = props => {
  const offices = useSelector(store => store.admin.resources.offices.data)
  const games = useSelector(state => state.admin.resources.games.data);
  const { values } = useFormState();
  const { officeId, gameId } = values;
  if (!offices || !officeId || !games || !gameId) return null;

  const office = offices[officeId]
  const game = gameId && games && games[gameId]
  if (!office || !game) return null;

  const seasonId = game.seasonId;
  return <AssignFeesInput {...props} filter={{ seasonId, effectiveOffices: officeId, includeParents: true, _scope: 'Parents' }} />
}

export const hasPosition = (systems, position) => value => {
  if (!value) return false;
  const system = systems[value];
  if (!system) return false;
  return system[position] > 0;
}

const AssignerDeterminationAlert = ({ systems }) => {
  const assignSettings = useRecordContext();
  const translate = useRichTranslate();
  const { data: assignRule } = useQuery({
    type: GET_ONE,
    resource: 'assignRules',
    payload: {
      id: assignSettings?.ruleId,
    }
  }, {
    enabled: !!assignSettings?.ruleId,
  })

  if (!assignRule) return null;
  return <SquareAlert severity="info">
    <RecordContextProvider value={assignRule}>
      {translate('resources.assignRules.alerts.determined_by', { rule: <AssignRuleField />, office: <AssignOfficeField source="officeId" link={false} /> })}
    </RecordContextProvider>
  </SquareAlert>
}

export const AssignSettingsForm = ({ onClose, ...props }) => {
  const isEnabled = useFlag();
  const systems = useSelector(state => state.admin.resources.assignSystems.data);

  return <>
    <AssignerDeterminationAlert systems={systems} />
    <SimpleForm toolbar={<Toolbar />} validate={validate} {...props} {...inputProps}>
      <Grid container spacing={1} fullWidth>
        <Grid item xs={12} md={4}>
          <SystemInput source="systemId" {...inputProps} />
        </Grid>
        <Grid item xs={12} md={4}>
          <OfficeInput source="officeId" filter={{ type: { neq: 'Historical' }, isAssigner: true, _scope: 'Tenant' }} {...inputProps} />
        </Grid>
        <Grid item xs={12} md={4}>
          <AssignmentStatusEnumInput source="status" {...inputProps} />
        </Grid>
        <FieldDependency fieldSource="systemId" test={hasPosition(systems, 'referees')}>
          <Grid item xs={12} md={4}>
            <NumberInput source="minReferee" min={0} step={1} helperText="ra.message.optional" {...inputProps} />
          </Grid>
        </FieldDependency>
        <FieldDependency fieldSource="systemId" test={hasPosition(systems, 'linespersons')}>
          <Grid item xs={12} md={4}>
            <NumberInput source="minLinesperson" min={0} step={1} helperText="ra.message.optional" {...inputProps} />
          </Grid>
        </FieldDependency>
        {isEnabled(FF_OFFICIALS) && <FieldDependency fieldSource="systemId" test={hasPosition(systems, 'officials')}>
          <Grid item xs={12} md={4}>
            <NumberInput source="minOfficial" min={0} step={1} helperText="ra.message.optional" {...inputProps} />
          </Grid>
        </FieldDependency>}
        {isEnabled(FF_SCOREKEEPERS) && <FieldDependency fieldSource="systemId" test={hasPosition(systems, 'scorekeepers')}>
          <Grid item xs={12} md={4}>
            <NumberInput source="minScorekeeper" min={0} step={1} helperText="ra.message.optional" {...inputProps} />
          </Grid>
        </FieldDependency>}
        {isEnabled(FF_TIMEKEEPERS) && <FieldDependency fieldSource="systemId" test={hasPosition(systems, 'timekeepers')}>
          <Grid item xs={12} md={4}>
            <NumberInput source="minTimekeeper" min={0} step={1} helperText="ra.message.optional" {...inputProps} />
          </Grid>
        </FieldDependency>}
        {isEnabled(FF_GRADES) && <FieldDependency fieldSource="systemId" test={hasPosition(systems, 'referees')}>
          <Grid item xs={12} md={4}>
            <NumberInput source="minRefereeGrade" min={0} step={1} helperText="ra.message.optional" {...inputProps} />
          </Grid>
        </FieldDependency>}
        {isEnabled(FF_GRADES) && <FieldDependency fieldSource="systemId" test={hasPosition(systems, 'linespersons')}>
          <Grid item xs={12} md={4}>
            <NumberInput source="minLinespersonGrade" min={0} step={1} helperText="ra.message.optional" {...inputProps} />
          </Grid>
        </FieldDependency>}
        <Grid item xs={12} md={4}>
          <NumberInput source="minAge" min={1} step={1} helperText="ra.message.optional" {...inputProps} />
        </Grid>
        <Grid item xs={12} md={4}>
          <FeesInput source="feesId" allowEmpty helperText="ra.message.optional" {...inputProps} />
        </Grid>
      </Grid>
    </SimpleForm>
  </>
}
